/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IAssessmentsConfig, IQueuesConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/api/api';
import { TaskService } from 'app/core/services/task-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { safe } from 'app/shared/lib/utilities';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as Core from 'app/core/models/essette-api';
import { defer } from 'app/shared/lib/decorators';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { EventAggregator } from 'aurelia-event-aggregator';
import { StoreFocusEvent, ReturnFocusEvent } from 'app/shared/lib/events';
import { AddTasks } from 'app/core/components/tasks/add-tasks';


export type TaskSelectCustomEvent = { item: Core.Task };

@autoinject()
export class TasksGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public tasksConfig!: ITasksConfig;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public queuesConfig?: IQueuesConfig;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public assessmentsConfig?: IAssessmentsConfig;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public includeInactive: boolean = false;

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected tasks: Array<Core.Task> = [];

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public showRouteLink: boolean = true;

    // When minimal, hide the Add Tasks/Show Inactive buttons
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected minimal: boolean = false;

    @bindable.stringNull()
    public key: string | null = null;

    protected hasTaskRoute: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected descriptionPreviewLength = 200;

    protected validate = validate;
    protected isValid: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $tasksForm!: Element;
    protected $saveTask!: AddTasks;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly service: TaskService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // Check that config property is bound
        if(!(this.targetId && this.tasksConfig)) {
            this.logger.warn("Target ID and/or Tasks Configuration not found!");
        }

        this.service.configure(this.tasksConfig);

        this.checkPermissions();

        // Only query tasks if none were bound
        if(!this.tasks.length && this.targetId) {
            this.loadTasks();
        }
        else {
            this.resultCount = this.tasks.length;
            this.tasks.splice(this.pageSize);
        }
    }

    protected attached(): void {
        if(this.$tasksForm) {
            this.isValid = validate(this.$tasksForm);
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload(false);
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected async formSuccess({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('core:task.$', true);
        this.$modal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        if(this.tasksConfig.taskDetailRouteName) {
            this.hasTaskRoute = this.accountContext.hasRouteAccess(this.tasksConfig.taskDetailRouteName);
        }
    }

    protected buildDelegatedToMessage(task: Core.Task): string | null {
        const delegated = safe(() => task.delegated);
        if(!(delegated && delegated.queue && delegated.queue.$display)) { return null; }

        const stamp = task.metadata && task.metadata.delegated;
        if(!stamp) { return null; }

        const label = this.i18n.tr(this.tasksConfig.labelKey);
        const queue = delegated.queue.$display;
        const queueUser = (delegated.to && delegated.to.$display) || null;
        const key = queueUser ? 'core:messages.task.delegated.user' : 'core:messages.task.delegated.queue';

        // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
        return this.i18n.tr(key, { what: label, queue: queue, who: queueUser, stamp: stamp });
    }

    protected buildCreatedByMessage(task: Core.Task): string | null {
        const stamp = task.metadata?.created;
        const label = this.i18n.tr(this.tasksConfig.labelKey);

        if(task.metadata?.triggeredBy?.description) {
            const key = 'core:messages.task.triggeredBy'
            const source = task.metadata.triggeredBy.description;
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { what: label, source: source, stamp: stamp });
        }
        else {
            const key = 'core:messages.task.created'
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { what: label, stamp: stamp });
        }
    }

    protected buildDetailRouteParams(taskId: string): Object {
        if(!this.tasksConfig.primaryTargetType) { return {}; }

        if(this.tasksConfig.primaryTargetType === 'CM.CaseTask') {
            return { taskId: taskId };
        }
        else if(this.tasksConfig.primaryTargetType === 'CM.CarePlanIntervention') {
            return { goalId: this.targetId, taskId: taskId };
        }
        else {
            return {};
        }
    }

    public clear(): void {
        this.tasks.splice(0);
    }

    public async reload(reloadCurrentTasks: boolean = true): Promise<void> {
        this.checkPermissions();
        await this.loadTasks();
        if(reloadCurrentTasks && this.$saveTask && this.tasksConfig.primaryTargetType === 'CM.CaseTask') this.$saveTask.loadCurrentTasks();
    }

    @defer
    public deferReload(): void {
        this.reload();
    }

    protected addTasks(): void {
        if(!this.targetId) {
            throw new Error('Missing required property: target id');
        }
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTasks(): Promise<void> {
        if(!this.targetId) return;

        const includes: string[] = [
            'definition',
            'definition.type',
            'delegated.queue',
            'delegated.to',
            'externalSystem',
            'metadata.activeChanged.byUser',
            'metadata.completed.byUser',
            'metadata.created.byUser',
            'metadata.delegated.byUser',
            'metadata.dueDateTimeSet.byUser'
        ];

        const fields: string[] = [
            'definition.id',
            'definition.type.code',
            'definition.type.description',
            'delegated.queue.description',
            'delegated.to.description',
            'description',
            'due.date',
            'externalSystem.code',
            'isCompleted',
            'metadata.activeChanged.timestamp',
            'metadata.activeChanged.byUser.name.full',
            'metadata.completed.timestamp',
            'metadata.completed.byUser.name.full',
            'metadata.created.timestamp',
            'metadata.created.byUser.description',
            'metadata.created.byUser.name.full',
            'metadata.delegated.timestamp',
            'metadata.delegated.byUser.name.full',
            'metadata.dueDateTimeSet.timestamp',
            'metadata.dueDateTimeSet.byUser.name.full'
        ];
        if(this.tasksConfig.primaryTargetType === 'CM.CarePlanIntervention') {
            includes.push('summary');
            fields.push('summary.taskSummary');
        }
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };
        try {
            this.state.beginLoad();

            const response = await this.service.loadTasksByTarget(this.targetId, options, includes, true, fields);
            this.tasks = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.events.publish(new ReturnFocusEvent());
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateTask(taskId: string, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate(this.tasksConfig.labelKey || 'core:task.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateTask(taskId, isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate(this.tasksConfig.labelKey || 'core:task.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }

}

